/* eslint-disable */
import { useState, useCallback, ReactNode, useMemo, useEffect } from 'react';
import { DataGridProProps, GridSortDirection } from '@mui/x-data-grid-pro';
import { useQuery } from 'react-query';

import { Box, TablePagination } from '@mui/material';
import { SxProps } from '@mui/system';

import { FilterButton, TableSearch, SortingButton } from '@molecules';
import { Axios, formatMidnightQueries, formatQueries, queryClient } from '@helpers';
import { FilterItem, MidnightQueries, Queries, SortingItem, TableSelectType } from '@services';
import { Loader, NoRowsOverlay } from '@atoms';
import { useTableQueries } from '@hooks';
import { TableWrap } from '@templates';
import { SelectedRow } from './SelectedRow';
import CollapseTableRow from './CollapseTableRow/CollapseTableRow';
import CollapseTableUtilities from './CollapseTableUtilities';
import { ApiClientEnterprise } from '@helpers/apiClients';
import { TQueryResponseCombineTable } from './types';
import { handleGetDataBasedOnResponse } from './utils';
import { EDataTableType } from './enum';
import { AxiosInstance } from 'axios';

const EXPANDED_ALL_VALUE = 1;

interface TableProps<T> extends Omit<DataGridProProps, 'onRowClick' | 'rows'> {
  columns: any;
  queryName?: string;
  apiClient?: AxiosInstance;
  apiRoute?: string;
  filtersList?: FilterItem[];
  sortingList?: SortingItem[];
  onRowClick?: (row: T) => void;
  selectedRowType?: TableSelectType;
  getSelectedValue?: (items: T[], selectedItems: string[]) => string;
  getSelectedMultipleValue?: (items: T[], selectedItems: string[]) => any;
  checkboxSelection?: boolean;
  refetch?: () => void;
  refetchQueryName?: string;
  searchPlaceholder?: string;
  withoutSearch?: boolean;
  preSelectedFilters?: { [key in string]: string | undefined };
  defaultQueries?: Partial<Queries>;
  tableSx?: SxProps;
  renderAdditionalControl?: ReactNode;
  transformData?: any;
  defaultPageSize?: number;
  renderRowPerPageOptions?: boolean;
  isEnterprise?: boolean;
  usingMidnightResponse?: boolean;
}

export const CollapseTable = <T extends object>({
  queryName = 'name',
  apiClient,
  apiRoute = '',
  columns,
  filtersList,
  sortingList,
  onRowClick,
  selectedRowType,
  getSelectedValue = () => '',
  // eslint-disable-next-line
  getSelectedMultipleValue = () => {},
  checkboxSelection,
  refetch: addictionalRefetch,
  refetchQueryName,
  searchPlaceholder,
  withoutSearch,
  preSelectedFilters,
  defaultQueries,
  tableSx,
  renderAdditionalControl,
  defaultPageSize,
  renderRowPerPageOptions = true,
  transformData,
  isEnterprise = false,
  pinnedColumns,
  usingMidnightResponse = false,
  ...others
}: TableProps<T>) => {
  const [selectedItems, setSelectedItems] = useState<Array<string>>([]);
  const [selectedItemsRecord, setSelectedItemsRecord] = useState<Record<string, string[]>>({});

  const [checkedAll, setCheckedAll] = useState<boolean>(false);
  const [expandedAll, sexExpandedAll] = useState<number>(EXPANDED_ALL_VALUE); // > 0 - expanded, 0 < - collapsed

  const {
    queries,
    setQueries,
    filterChange,
    resetFilter,
    filters,
    setFilters,
    selectedFilters,
    setSelectedFilters,
  } = useTableQueries(preSelectedFilters, defaultQueries, isEnterprise, usingMidnightResponse);

  const { data, isLoading, refetch } = useQuery<unknown, unknown, TQueryResponseCombineTable<T>>(
    [queryName, queries, filters],
    () => {
      if (!apiRoute) {
        return;
      }

      const client = apiClient || (isEnterprise ? ApiClientEnterprise : Axios);

      return client
        .get(apiRoute, {
          params: usingMidnightResponse
            ? formatMidnightQueries({ ...queries, ...filters } as MidnightQueries)
            : formatQueries({ ...queries, ...filters } as Queries),
        })
        .then((res) => res.data);
    },
  );

  const dataItems = useMemo(() => {
    if (data) {
      return handleGetDataBasedOnResponse<T>(
        data,
        usingMidnightResponse,
        EDataTableType.DataItem,
      ) as T[];
    }
    return [];
  }, [data, usingMidnightResponse]);

  const totalCountData = useMemo(() => {
    if (data) {
      return handleGetDataBasedOnResponse<T>(
        data,
        usingMidnightResponse,
        EDataTableType.TotalCount,
      ) as number;
    }
    return 0;
  }, [data, usingMidnightResponse]);

  const size = useMemo(() => {
    return (
      (handleGetDataBasedOnResponse<T>(
        queries,
        usingMidnightResponse,
        EDataTableType.Size,
      ) as number) || 10
    );
  }, [queries, usingMidnightResponse]);

  const totalCount = data ? Math.min(dataItems.length, totalCountData) : 0;
  const rowsPerPageOptions =
    others.rowsPerPageOptions ||
    (totalCount
      ? [10, 20, 50, 100, totalCount]
          .filter((item) => item <= totalCount)
          .filter((v, i, a) => a.indexOf(v) === i) // get unique
      : [10, 20, 50, 100]);

  useEffect(() => {
    const newSelectedItems = Object.entries(selectedItemsRecord)
      .map(([, value]) => {
        return [...value];
      })
      .flat();

    setSelectedItems(newSelectedItems);
    setCheckedAll(totalCount > 0 && newSelectedItems.length === totalCount);
  }, [selectedItemsRecord, totalCount]);

  const handleRefetch = () => {
    refetch();
    setSelectedItems([]);
    setSelectedItemsRecord({});
    // eslint-disable-next-line no-unused-expressions
    addictionalRefetch && addictionalRefetch();
    if (refetchQueryName) {
      queryClient.refetchQueries(refetchQueryName);
    }
  };

  const noRows = useCallback(
    () => (
      <NoRowsOverlay
        filterText={queries.filterText}
        hasSelectedFilters={!!selectedFilters.length}
      />
    ),
    [queries.filterText, selectedFilters],
  );

  const items = useMemo(
    () =>
      (transformData && transformData(dataItems)) ||
      dataItems?.map((item: any, index) => ({ id: index, ...item })) ||
      [],
    [transformData, dataItems],
  );
  const hasSelectedFilters = filtersList?.some(({ key }) => selectedFilters.includes(key));
  const selected = getSelectedValue(items, selectedItems);
  const multipleSelected = getSelectedMultipleValue(items, selectedItems);

  return (
    <>
      {!withoutSearch && (
        <Box display="flex" gap="16px" mb="12px" alignItems="center">
          <CollapseTableUtilities
            checkedAll={checkedAll}
            onCheckAll={(event: React.ChangeEvent<HTMLInputElement>) => {
              if (event.target.checked) {
                const newSelectedItemsRecord = items.reduce(
                  (acc: { [key: number]: string[] }, curr: any) => {
                    acc[curr.id] = curr.child.map((item: any) => item.id);
                    return acc;
                  },
                  {},
                );
                setSelectedItemsRecord(newSelectedItemsRecord);
              } else {
                setSelectedItemsRecord({});
              }
            }}
            onExpandAll={() => {
              if (expandedAll >= EXPANDED_ALL_VALUE) {
                sexExpandedAll((prev) => prev + 1);
              } else {
                sexExpandedAll(1);
              }
            }}
            onCollapseAll={() => {
              if (expandedAll <= -EXPANDED_ALL_VALUE) {
                sexExpandedAll((prev) => prev - 1);
              } else {
                sexExpandedAll(-EXPANDED_ALL_VALUE);
              }
            }}
          />
          <TableSearch
            placeholder={searchPlaceholder}
            defaultSearch={queries.filterText}
            onSearch={(filterText) =>
              setQueries((prev: Queries | MidnightQueries) => ({
                ...prev,
                filterText,
                page: isEnterprise || usingMidnightResponse ? 1 : 0,
              }))
            }
          />
          {filtersList && (
            <FilterButton
              options={filtersList.filter(({ key }) => !selectedFilters.includes(key))}
              onSelect={(key) => setSelectedFilters([...selectedFilters, key])}
              hasSelected={hasSelectedFilters}
              disabled={!filtersList.filter(({ key }) => !selectedFilters.includes(key)).length}
            />
          )}
          {sortingList && (
            <SortingButton
              options={sortingList}
              onSelect={(sortBy: any, sortDirection: GridSortDirection) =>
                setQueries({
                  ...queries,
                  sortBy,
                  ...(usingMidnightResponse ? { sortOrder: sortDirection } : { sortDirection }),
                })
              }
            />
          )}
          {renderAdditionalControl}
        </Box>
      )}
      {filtersList && !!selectedFilters?.length && (
        <Box display="flex" gap="24px" mb="24px">
          {filtersList?.map(({ key, getFilter, resetFields }) => {
            if (selectedFilters?.includes(key)) {
              return getFilter(
                filterChange,
                () => {
                  resetFilter(resetFields || [key]);
                  setSelectedFilters(selectedFilters.filter((f: string) => f !== key));
                },
                setFilters,
                filters,
              );
            }

            return null;
          })}
        </Box>
      )}
      {!!selectedItems?.length && (
        <SelectedRow
          value={selected}
          multipleValue={multipleSelected}
          items={selectedItems}
          count={selectedItems.length}
          cancel={() => {
            setSelectedItemsRecord({});
            setSelectedItems([]);
          }}
          refetch={handleRefetch}
          type={selectedRowType}
        />
      )}
      <TableWrap
        height="550px"
        sx={{
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          ...tableSx,
        }}
      >
        <Loader isLoading={isLoading} sx={{ minHeight: 'unset' }}>
          <>
            {!isLoading && items.length === 0 && noRows()}
            {items.map((item: any, index: number) => (
              <CollapseTableRow
                key={index}
                row={item}
                columns={columns}
                pinnedColumns={pinnedColumns}
                onRowClick={onRowClick}
                checkboxSelection={checkboxSelection}
                expandedAll={expandedAll}
                setSelectedItemsRecord={setSelectedItemsRecord}
                selectedItems={selectedItemsRecord?.[item?.id]}
                selectedRowIndex={index}
                items={items}
                selectedItemsRecord={selectedItemsRecord}
              />
            ))}
          </>
        </Loader>
      </TableWrap>
      <TablePagination
        sx={{
          marginTop: '8px',
        }}
        component="div"
        count={totalCountData || 0}
        page={isEnterprise ? queries.page - 1 : queries.page}
        onPageChange={(_event: React.MouseEvent<HTMLButtonElement> | null, page: number) =>
          setQueries({ ...queries, page: isEnterprise ? page + 1 : page })
        }
        onRowsPerPageChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setQueries({
            ...queries,
            ...(usingMidnightResponse
              ? {
                  pageSize: parseInt(event.target.value, 10),
                  page: isEnterprise || usingMidnightResponse ? 1 : 0,
                }
              : {
                  size: parseInt(event.target.value, 10),
                  page: isEnterprise || usingMidnightResponse ? 1 : 0,
                }),
          });
        }}
        rowsPerPage={defaultPageSize || size}
        rowsPerPageOptions={renderRowPerPageOptions ? rowsPerPageOptions : []}
      />
    </>
  );
};
